const KNOWLEDGE_BASE_BASE_URL = 'https://support.scileads.com/en/article';

/**
 * Builds the complete knowledge base URL with HMAC authentication
 * @param {string} articleSlug - The article slug from the URL parameter
 * @param {string} hmacToken - The HMAC authentication token
 * @returns {string} The complete URL to redirect to
 */
export const buildKnowledgeBaseUrl = (articleSlug, hmacToken) => {
  if (!articleSlug || !hmacToken) {
    throw new Error('Both articleSlug and hmacToken are required');
  }

  const sanitizedSlug = sanitizeArticleSlug(articleSlug);
  return `${KNOWLEDGE_BASE_BASE_URL}/${sanitizedSlug}?hmac=${hmacToken}`;
};

/**
 * Sanitizes and validates the article slug parameter
 * @param {string} articleSlug - The raw article slug from URL
 * @returns {string} The sanitized article slug
 */
export const sanitizeArticleSlug = articleSlug => {
  if (!articleSlug || typeof articleSlug !== 'string') {
    throw new Error('Article slug must be a non-empty string');
  }

  // Remove any potentially harmful characters while preserving valid slug characters
  // Allow letters, numbers, hyphens, and underscores
  const sanitized = articleSlug.replace(/[^a-zA-Z0-9\-_]/g, '');

  if (!sanitized) {
    throw new Error('Article slug contains no valid characters');
  }

  return sanitized;
};

/**
 * Creates log data for knowledge base redirect errors
 * @param {string} articleSlug - The article being accessed
 * @param {string} outcome - 'Success' or 'Failure'
 * @param {string} description - Additional details about the error
 * @returns {Object} Log data object for API submission
 */
export const createKnowledgeBaseErrorLog = (
  articleSlug,
  outcome,
  description
) => {
  return {
    description: `KB Redirect: ${articleSlug} - ${description}`,
    action: 'UIError',
    outcome,
  };
};
